import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Heart, MapPin, Calendar, Trees, Tent, Flame, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { settingsAPI, locationsAPI } from '../utils/api';

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [settings, setSettings] = useState({});
  const [featuredLocations, setFeaturedLocations] = useState([]);

  useEffect(() => {
    settingsAPI.get().then((r) => setSettings(r.data)).catch(() => {});
    locationsAPI.getRandomFeatured(3)
      .then((r) => setFeaturedLocations(r.data || []))
      .catch(() => setFeaturedLocations([]));
  }, []);

  const v = (key, fallback) => settings[key] || fallback;

  const handleSearch = () => {
    const queryParams = new URLSearchParams();
    if (searchQuery) queryParams.set('search', searchQuery);
    navigate(`/locations?${queryParams.toString()}`);
  };

  const handleShowMap = () => {
    const queryParams = new URLSearchParams();
    queryParams.set('view', 'map');
    if (searchQuery) queryParams.set('search', searchQuery);
    navigate(`/locations?${queryParams.toString()}`);
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center pt-48">
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight drop-shadow-2xl whitespace-pre-line" data-testid="home-hero-title">
            {v('home_hero_title', 'NAJDI SVÉ IDEÁLNÍ MÍSTO\nVE VOLNÉ PŘÍRODĚ')}
          </h1>
          <p className="text-base sm:text-lg text-white/90 mb-8 max-w-2xl mx-auto drop-shadow-lg whitespace-pre-line" data-testid="home-hero-subtitle">
            {v('home_hero_subtitle', 'Nocování v divočině, daleko od civilizace. Najdi a rezervuj\nnejlepší místa pro divoké campingové dobrodružství.')}
          </p>

          {/* Search Box */}
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/30">
            {/* Show Map – primary CTA at the top */}
            <Button
              onClick={handleShowMap}
              className="w-full h-14 bg-orange-600 hover:bg-orange-700 text-white font-bold text-base shadow-lg mb-4"
              data-testid="home-show-map-btn"
            >
              <MapPin className="w-5 h-5 mr-2" />
              ZOBRAZIT MAPU
            </Button>

            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white" />
                <Input
                  type="text"
                  placeholder="Vyber místo nebo lokalitu..."
                  className="pl-10 h-12 text-base bg-white/30 border-white/40 text-white placeholder:text-white/70 focus:bg-white/40"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button
                onClick={handleSearch}
                className="h-12 px-8 bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-lg"
              >
                HLEDAT
              </Button>
            </div>

            <p className="text-xs text-white/80 mt-3 text-center">
              💡 Filtry (chůze / offroad / dodávka …) najdete po zobrazení mapy nebo seznamu míst.
            </p>
          </div>

          {/* Add Location Button – big CTA for new hosts */}
          <div className="mt-10">
            <Button
              onClick={() => navigate('/add-location')}
              className="bg-gradient-to-r from-orange-500 to-orange-700 hover:from-orange-600 hover:to-orange-800 text-white px-6 sm:px-10 lg:px-14 py-5 sm:py-7 lg:py-8 text-sm sm:text-lg lg:text-xl font-extrabold shadow-2xl rounded-2xl tracking-wide border-2 border-orange-300/30 ring-4 ring-orange-500/20 hover:ring-orange-500/40 hover:scale-105 transition-all duration-200 h-auto max-w-full whitespace-normal text-center leading-snug"
              data-testid="home-add-location-btn"
            >
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 shrink-0" />
              <span>{v('home_cta_owner_title', 'JSI MAJITEL POZEMKU? NABÍDNI SVÉ MÍSTO')}</span>
            </Button>
            <p className="mt-3 text-sm sm:text-base lg:text-lg font-semibold text-orange-200 drop-shadow-lg tracking-wide" data-testid="home-hosts-wanted">
              ✨ {v('home_cta_owner_subtitle', 'Hledáme nové hostitele')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg" data-testid="home-features-title">
            {v('home_features_title', 'ODLEHLÁ MÍSTA K NOCOVÁNÍ')}
          </h2>
          <p className="text-lg text-white/90 max-w-3xl mx-auto drop-shadow-md" data-testid="home-features-subtitle">
            {v('home_features_subtitle', 'Objev ta nejlepší odlehlá místa pro bezpečné kempování ve veřejné přírodě. Daleko od davu, v nádherné, divoké přírodě.')}
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard
            icon={<Trees className="w-12 h-12" />}
            title={v('home_feat1_title', 'ODLEHLÁ PŘÍRODA')}
            description={v('home_feat1_text', 'Nocování v opravdové divočině, daleko od civilizace.')}
          />
          <FeatureCard
            icon={<Truck className="w-12 h-12" />}
            title={v('home_feat2_title', 'JEĎ OFFROAD')}
            description={v('home_feat2_text', 'Místa dostupná autem nebo terénním vozem.')}
          />
          <FeatureCard
            icon={<Tent className="w-12 h-12" />}
            title={v('home_feat3_title', 'POSTAV STAN')}
            description={v('home_feat3_text', 'Krásná místa pro stanování pod hvězdami.')}
          />
          <FeatureCard
            icon={<Flame className="w-12 h-12" />}
            title={v('home_feat4_title', 'ZAPAL OHEŇ')}
            description={v('home_feat4_text', 'Místa, kde si můžete legálně rozdělat oheň.')}
          />
        </div>
      </section>

      {/* Featured Locations */}
      {featuredLocations.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredLocations.map((location) => (
                <LocationCard key={location.id} location={location} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg" data-testid="home-hiw-title">
            {v('home_hiw_title', 'JAK TO FUNGUJE')}
          </h2>
          <p className="text-lg text-white/90 mb-16 drop-shadow-md" data-testid="home-hiw-subtitle">
            {v('home_hiw_subtitle', 'Chcete si užít odlehlou přírodu? Je to jednoduché!')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <StepCard
              number="1"
              title={v('home_hiw_step1_title', 'NAJDI MÍSTO POD HVĚZDAMI')}
              description={v('home_hiw_step1_text', 'Objev jedinečná místa v přírodě daleko od civilizace.')}
            />
            <StepCard
              number="2"
              title={v('home_hiw_step2_title', 'REZERVUJ PLAC MEZI STROMY')}
              description={v('home_hiw_step2_text', 'Vyber termín a uteč daleko od civilizace.')}
            />
            <StepCard
              number="3"
              title={v('home_hiw_step3_title', 'ZBAL KROSNU A VYRAŽ DO DIVOČINY')}
              description={v('home_hiw_step3_text', 'Ticho lesa, zpěv ptáků a praskání ohně v divočině.')}
            />
          </div>

          {/* Signature image – author quote + handwritten signature */}
          <div className="mt-12 flex justify-center" data-testid="home-hiw-signature">
            <img
              src="https://customer-assets.emergentagent.com/job_elementor-site-1/artifacts/giy5q43b_0fe206a6-2bba-48fc-aeca-b2c9c9699c2d.png"
              alt="WildCamping — Tomáš E."
              loading="lazy"
              className="w-full max-w-3xl h-auto opacity-95 hover:opacity-100 transition-opacity duration-500"
              style={{
                mixBlendMode: 'lighten',
                filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.6))',
              }}
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <Card className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer group">
    <CardContent className="p-6 text-center">
      <div className="mb-4 text-orange-500 flex justify-center group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
      <p className="text-white/80 text-sm">{description}</p>
    </CardContent>
  </Card>
);

const LocationCard = ({ location }) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);

  return (
    <Card
      className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
      onClick={() => navigate(`/location/${location.id}`)}
      data-testid={`home-location-card-${location.id}`}
    >
      <div className="relative h-64 overflow-hidden">
        <img
          src={location.photos?.[0] || location.image || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'}
          alt={location.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <button
          onClick={(e) => { e.stopPropagation(); setIsFavorite(!isFavorite); }}
          className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all"
        >
          <Heart
            className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
          />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <div className="text-white font-semibold text-sm">{location.type}</div>
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="text-2xl font-bold mb-2">{location.name}</h3>
        <p className="text-gray-600 mb-4 text-sm">{location.description}</p>
        <Button
          onClick={(e) => { e.stopPropagation(); navigate(`/location/${location.id}`); }}
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold"
        >
          VÍCE INFORMACÍ
        </Button>
      </CardContent>
    </Card>
  );
};

const StepCard = ({ number, title, description }) => (
  <div
    className="group relative text-center bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 transition-all duration-500 ease-out hover:bg-white/15 hover:-translate-y-2 hover:scale-[1.03] hover:border-orange-400/60 hover:shadow-[0_0_40px_-5px_rgba(249,115,22,0.55)] cursor-default overflow-hidden"
    data-testid={`home-hiw-step-${number}`}
  >
    {/* Animated orange glow blur behind the card on hover */}
    <div className="pointer-events-none absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl bg-gradient-to-br from-orange-500/30 via-amber-400/10 to-transparent" />
    {/* Subtle moving light sweep on hover */}
    <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700">
      <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.08),transparent_40%)] animate-[spin_18s_linear_infinite]" />
    </div>
    <div className="relative">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-600 text-white text-2xl font-bold mb-4 shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(249,115,22,0.7)]">
        {number}
      </div>
      <h3 className="text-white font-bold text-xl mb-3 transition-colors duration-300 group-hover:text-orange-100">{title}</h3>
      <p className="text-white/80 transition-colors duration-300 group-hover:text-white/95">{description}</p>
    </div>
  </div>
);

export default Home;
